import * as OtpService from '../service/otpService';

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getOrRefreshAccessToken } from './authenticationSlice';
import { displayErrorNotification } from './notificationSlice';
import type { AppThunk, RootState } from './types';

import CountryPrefix from 'types/CountryPrefix';
import ReadCountryPrefixesResponse from 'types/ReadCountryPrefixesResponse';
import UpdateCountryPrefixRequest from 'types/UpdateCountryPrefixRequest';
import UpdateCountryPrefixResponse from 'types/UpdateCountryPrefixResponse';

export enum OtpCountryPrefixManagementStatus {
  Idle = 'Idle',
  Loading = 'Loading',
  Success = 'Success',
}

/* Reducer */
export interface ReadCountryPrefixesState {
  countryCodeOrPrefix: string;
  countryPrefixes: CountryPrefix[] | undefined;
  status: OtpCountryPrefixManagementStatus;
}

export const initialReadCountryPrefixesState = {
  countryCodeOrPrefix: '',
  countryPrefixes: undefined,
  status: OtpCountryPrefixManagementStatus.Idle,
} as ReadCountryPrefixesState;

export const otpReadCountryPrefixesSlice = createSlice({
  name: 'otpReadCountryPrefix',
  initialState: initialReadCountryPrefixesState,
  reducers: {
    readCountryPrefixesStart: (state: ReadCountryPrefixesState) => {
      state.status = OtpCountryPrefixManagementStatus.Loading;
      state.countryPrefixes = undefined;
    },
    readCountryPrefixesSuccess: (
      state: ReadCountryPrefixesState,
      action: PayloadAction<ReadCountryPrefixesResponse>,
    ) => {
      state.status = OtpCountryPrefixManagementStatus.Success;
      state.countryPrefixes = action.payload?.countryPrefixes;
    },
    readCountryPrefixesError: (state: ReadCountryPrefixesState) => {
      state.status = OtpCountryPrefixManagementStatus.Idle;
      state.countryPrefixes = undefined;
    },
    setCountryCodeOrPrefix: (state, action: PayloadAction<string>) => {
      state.countryCodeOrPrefix = action.payload;
    },
    updateCountryPrefixInList: (
      state: ReadCountryPrefixesState,
      action: PayloadAction<UpdateCountryPrefixResponse>,
    ) => {
      const updatedCountryPrefix = action.payload;
      const countryPrefix = state.countryPrefixes?.find(
        (countryPrefix) => countryPrefix.countryCode === updatedCountryPrefix.countryCode,
      );

      if (countryPrefix) {
        countryPrefix.isEnabled = updatedCountryPrefix.isEnabled;
        countryPrefix.isUpdatedBySystem = updatedCountryPrefix.isUpdatedBySystem;
        countryPrefix.lastUpdateTimestamp = updatedCountryPrefix.lastUpdateTimestamp;
      }
    },
  },
});

export interface UpdateCountryPrefixState {
  status: OtpCountryPrefixManagementStatus;
  countryCode: string | undefined;
  isEnabled: boolean | undefined;
  response: UpdateCountryPrefixResponse | undefined;
}

export const initialUpdateCountryPrefixState = {
  status: OtpCountryPrefixManagementStatus.Idle,
  countryCode: undefined,
  isEnabled: undefined,
  response: undefined,
} as UpdateCountryPrefixState;

export const otpUpdateCountryPrefixSlice = createSlice({
  name: 'otpUpdateCountryPrefix',
  initialState: initialUpdateCountryPrefixState,
  reducers: {
    updateCountryPrefixStart: (state: UpdateCountryPrefixState) => {
      state.status = OtpCountryPrefixManagementStatus.Loading;
      state.response = undefined;
    },
    updateCountryPrefixSuccess: (
      state: UpdateCountryPrefixState,
      action: PayloadAction<UpdateCountryPrefixResponse>,
    ) => {
      state.status = OtpCountryPrefixManagementStatus.Success;
      state.response = action.payload;
    },
    updateCountryPrefixError: (state: UpdateCountryPrefixState) => {
      state.status = OtpCountryPrefixManagementStatus.Idle;
      state.response = undefined;
    },
  },
});

export const {
  readCountryPrefixesStart,
  readCountryPrefixesSuccess,
  readCountryPrefixesError,
  setCountryCodeOrPrefix,
  updateCountryPrefixInList,
} = otpReadCountryPrefixesSlice.actions;

export const { updateCountryPrefixStart, updateCountryPrefixSuccess, updateCountryPrefixError } =
  otpUpdateCountryPrefixSlice.actions;

/* Thunk */
export const readCountryPrefixes = (): AppThunk => async (dispatch, getState) => {
  if (selectOtpReadCountryPrefixesStatus(getState()) === OtpCountryPrefixManagementStatus.Loading) {
    return;
  }

  try {
    dispatch(readCountryPrefixesStart());
    const accessToken = await getOrRefreshAccessToken(dispatch, getState);
    const response = await OtpService.readCountryPrefixes(accessToken);

    dispatch(readCountryPrefixesSuccess(response));
  } catch (e) {
    dispatch(displayErrorNotification(e));
    dispatch(readCountryPrefixesError());
  }
};

export const updateCountryPrefix =
  (updateCountryPrefixRequest: UpdateCountryPrefixRequest): AppThunk =>
  async (dispatch, getState) => {
    if (selectOtpUpdateCountryPrefixStatus(getState()) === OtpCountryPrefixManagementStatus.Loading) {
      return;
    }

    try {
      dispatch(updateCountryPrefixStart());

      const accessToken = await getOrRefreshAccessToken(dispatch, getState);
      const response = await OtpService.updateCountryPrefix(updateCountryPrefixRequest, accessToken);

      dispatch(updateCountryPrefixSuccess(response));
      dispatch(updateCountryPrefixInList(response));
    } catch (e) {
      dispatch(displayErrorNotification(e));
      dispatch(updateCountryPrefixError());
      dispatch(readCountryPrefixes());
    }
  };

/* Selectors */
export const selectOtpReadCountryPrefixes = (state: RootState) => state.otpReadCountryPrefixes.countryPrefixes;
export const selectOtpReadCountryPrefixesStatus = (state: RootState) => state.otpReadCountryPrefixes.status;
export const selectCountryCodeOrPrefixFilter = (state: RootState) => state.otpReadCountryPrefixes.countryCodeOrPrefix;

export const selectOtpUpdateCountryPrefixResponse = (state: RootState) => state.otpUpdateCountryPrefix.response;
export const selectOtpUpdateCountryPrefixIsEnabled = (state: RootState) => state.otpUpdateCountryPrefix.isEnabled;
export const selectOtpUpdateCountryPrefixCountryCode = (state: RootState) => state.otpUpdateCountryPrefix.countryCode;
export const selectOtpUpdateCountryPrefixStatus = (state: RootState) => state.otpUpdateCountryPrefix.status;
